import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useCrew, useShifts, fetchCrewMemberByToken } from "@/hooks/admin/useAdminData";
import { useAuth } from "@/hooks/useAuth";
import type { CrewMember } from "@/mocks/admin/crew";
import MoverShell from "./components/MoverShell";
import MoverHeader from "./components/MoverHeader";
import MoverTabBar, { type MoverTab } from "./components/MoverTabBar";
import CrewSwitcherSheet from "./components/CrewSwitcherSheet";
import TodayTab from "./components/TodayTab";
import JobsTab from "./components/JobsTab";
import AvailabilityTab from "./components/AvailabilityTab";
import PayTab from "./components/PayTab";

const STORAGE_KEY = "haulmark.moverApp.activeCrewId";

export default function MoverAppPage() {
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  // When a token is present we lock to a single crew member (private link mode).
  // Without a token (demo route /m or /m/timeclock) we require an admin session
  // and let them switch between movers for preview/QA purposes.
  const isPrivateLink = !!token;
  const isAdminPreview = !isPrivateLink;

  const { data: crewMembers, loading: crewLoading, refetch: refetchCrew } = useCrew();
  const { active: liveShifts, refetch: refetchShifts } = useShifts();

  const [tokenMember, setTokenMember] = useState<CrewMember | null>(null);
  const [tokenLoading, setTokenLoading] = useState<boolean>(isPrivateLink);
  const [tokenError, setTokenError] = useState<string>("");

  const [demoActiveId, setDemoActiveId] = useState<string>("");
  const [tab, setTab] = useState<MoverTab>("today");
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [toast, setToast] = useState("");

  // Token-based lookup (private link)
  useEffect(() => {
    if (!isPrivateLink) return;
    let cancelled = false;
    setTokenLoading(true);
    setTokenError("");
    fetchCrewMemberByToken(token!)
      .then((m) => {
        if (cancelled) return;
        if (!m) {
          setTokenError("This link is invalid or has been revoked.");
        } else {
          setTokenMember(m);
          // Remember the token so the page works offline-ish too
          try {
            sessionStorage.setItem("haulmark.moverApp.token", token!);
          } catch {
            // ignore storage errors
          }
        }
      })
      .catch((e) => {
        if (!cancelled) setTokenError(e instanceof Error ? e.message : "Failed to load profile");
      })
      .finally(() => {
        if (!cancelled) setTokenLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPrivateLink, token]);

  // Refresh the token-bound member when crew data refetches (so availability updates show)
  useEffect(() => {
    if (!isPrivateLink || !tokenMember) return;
    const fresh = crewMembers.find((m) => m.id === tokenMember.id);
    if (fresh) setTokenMember(fresh);
  }, [crewMembers, isPrivateLink, tokenMember]);

  // Demo mode initial selection (admins only)
  useEffect(() => {
    if (isPrivateLink) return;
    if (!session) return; // don't load crew list for unauthenticated visitors
    if (demoActiveId || crewMembers.length === 0) return;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && crewMembers.some((m) => m.id === saved)) {
      setDemoActiveId(saved);
    } else {
      const onShift = crewMembers.find((m) =>
        liveShifts.some((s) => s.crewId === m.id),
      );
      setDemoActiveId(onShift?.id || crewMembers[0].id);
    }
  }, [crewMembers, liveShifts, demoActiveId, isPrivateLink]);

  const member = useMemo<CrewMember | null>(() => {
    if (isPrivateLink) return tokenMember;
    return crewMembers.find((m) => m.id === demoActiveId) || crewMembers[0] || null;
  }, [isPrivateLink, tokenMember, crewMembers, demoActiveId]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const switchTo = (id: string) => {
    setDemoActiveId(id);
    localStorage.setItem(STORAGE_KEY, id);
    setTab("today");
  };

  // --- Loading & error states ---

  // Admin preview route: block non-admins from seeing the crew switcher
  if (isAdminPreview && authLoading) {
    return (
      <MoverShell>
        <div className="flex-1 flex items-center justify-center text-ink-500 text-sm">
          <i className="ri-loader-4-line animate-spin mr-2"></i>
          Loading…
        </div>
      </MoverShell>
    );
  }

  if (isAdminPreview && !session) {
    return (
      <MoverShell>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-3">
          <div className="w-14 h-14 rounded-full bg-ink-900 text-white flex items-center justify-center">
            <i className="ri-shield-keyhole-line text-2xl"></i>
          </div>
          <div className="font-display text-lg font-bold text-ink-900">
            This isn’t your app
          </div>
          <p className="text-sm text-ink-500 max-w-xs leading-relaxed">
            The mover app uses a private personal link. Please ask your dispatcher to text or email you the link for your phone.
          </p>
          <div className="text-[11px] text-ink-400 mt-1 font-mono bg-cream-100 px-3 py-1.5 rounded-full">
            yourname.haulmark.co/m/…
          </div>
          <Link
            to="/login"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-bold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap"
          >
            <i className="ri-login-box-line"></i>
            Sign in as admin
          </Link>
        </div>
      </MoverShell>
    );
  }

  if (isPrivateLink && tokenLoading) {
    return (
      <MoverShell>
        <div className="flex-1 flex items-center justify-center text-ink-500 text-sm">
          <i className="ri-loader-4-line animate-spin mr-2"></i>
          Loading your profile…
        </div>
      </MoverShell>
    );
  }

  if (isPrivateLink && tokenError) {
    return (
      <MoverShell>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-3">
          <div className="w-14 h-14 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center">
            <i className="ri-link-unlink-m text-2xl"></i>
          </div>
          <div className="font-display text-lg font-bold text-ink-900">Link not valid</div>
          <p className="text-sm text-ink-500 max-w-xs">
            {tokenError} Please contact your dispatcher for a new link.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-2 text-xs font-semibold text-brand-700 hover:underline cursor-pointer"
          >
            Go to homepage
          </button>
        </div>
      </MoverShell>
    );
  }

  if (crewLoading && crewMembers.length === 0 && !isPrivateLink) {
    return (
      <MoverShell>
        <div className="flex-1 flex items-center justify-center text-ink-500 text-sm">
          <i className="ri-loader-4-line animate-spin mr-2"></i>
          Loading your shift…
        </div>
      </MoverShell>
    );
  }

  if (!member) {
    return (
      <MoverShell>
        <div className="flex-1 flex items-center justify-center text-ink-500 text-sm p-8 text-center">
          No crew accounts found. Add team members from the admin Crew page.
        </div>
      </MoverShell>
    );
  }

  return (
    <MoverShell>
      <MoverHeader
        member={member}
        onSwitch={isPrivateLink ? undefined : () => setSwitcherOpen(true)}
      />

      <main className="flex-1 overflow-y-auto scrollbar-thin">
        {tab === "today" && (
          <TodayTab
            member={member}
            shifts={liveShifts}
            refetchShifts={refetchShifts}
            onToast={showToast}
          />
        )}
        {tab === "jobs" && <JobsTab member={member} />}
        {tab === "availability" && (
          <AvailabilityTab member={member} onUpdated={refetchCrew} onToast={showToast} />
        )}
        {tab === "pay" && <PayTab member={member} />}
      </main>

      <MoverTabBar active={tab} onChange={setTab} />

      {!isPrivateLink && (
        <CrewSwitcherSheet
          open={switcherOpen}
          onClose={() => setSwitcherOpen(false)}
          members={crewMembers}
          currentId={member.id}
          onSelect={switchTo}
        />
      )}

      {/* Toast */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 bottom-24 z-[60] transition-all duration-300 ${
          toast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="bg-ink-900 text-white text-xs font-semibold px-4 py-2.5 rounded-full border border-ink-800 flex items-center gap-2 max-w-[90vw] md:max-w-xs">
          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
            <i className="ri-check-line text-xs"></i>
          </span>
          {toast}
        </div>
      </div>
    </MoverShell>
  );
}